/**
 * Страница управления настройками уведомлений пациентов (TODO-052)
 * Дата: 10.02.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ:
 * - api/patients.ts — API клиент
 * - components/ui/ChannelBadges — бейджи каналов
 * - components/features/patients/PatientChannelSelector — модал выбора канала
 */
import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import {
  Card,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TenantSelector,
  SearchInput,
  DateRangePicker,
  EmptyState,
  LoadingState,
  Pagination,
  ChannelBadges,
} from '@/components/ui';
import { PatientChannelSelector } from '@/components/features/patients/PatientChannelSelector';
import { PatientChannelFilters, type PatientChannelFilterValue } from '@/components/features/patients/PatientChannelFilters';
import { Can } from '@/components/Can';
import {
  patientsApi,
  type PatientPreferenceSourceDTO,
  type PatientPreferredChannelHistoryItemDTO,
  type PatientWithChannelsDTO,
} from '@/api/patients';
import { getChannelDisplayName } from '@/config/channels';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';
import { formatPhone, formatDateTime } from '@/utils/formatters';
import { startOfDay, endOfDay, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { Settings2 } from 'lucide-react';

export function PatientsPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const { showSuccess, showError } = useToast();

  const [patients, setPatients] = useState<PatientWithChannelsDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [channelFilters, setChannelFilters] = useState<PatientChannelFilterValue>({
    matchMode: 'any',
    channels: [],
    excludeChannels: [],
    preferredChannel: '',
    contactMode: '',
  });

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientWithChannelsDTO | null>(null);
  const [preferenceSources, setPreferenceSources] = useState<PatientPreferenceSourceDTO[]>([]);
  const [historyItems, setHistoryItems] = useState<PatientPreferredChannelHistoryItemDTO[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    logger.info('page_view', { page: 'patients' });
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setPreferenceSources([]);
      return;
    }

    let cancelled = false;

    const loadSources = async () => {
      try {
        const items = await patientsApi.listPreferenceSources();
        if (!cancelled) {
          setPreferenceSources(items);
        }
      } catch {
        if (!cancelled) {
          showError('Не удалось загрузить справочник источников');
        }
      }
    };

    void loadSources();
    return () => {
      cancelled = true;
    };
  }, [tenantId, showError]);

  // 10.02.2026 — РЕВЬЮ АРХИТЕКТОР: AbortController для защиты от race condition при быстром
  // переключении фильтров / tenantId (предыдущий запрос отменяется при новом)
  useEffect(() => {
    if (!tenantId) {
      setPatients([]);
      return;
    }

    let cancelled = false;

    const loadPatients = async () => {
      setIsLoading(true);
      try {
        const response = await patientsApi.list({
          search: searchQuery || undefined,
          last_visit_from: dateFrom ? dateFrom.toISOString().split('T')[0] : undefined,
          last_visit_to: dateTo ? dateTo.toISOString().split('T')[0] : undefined,
          match_mode: channelFilters.matchMode,
          channels: channelFilters.channels.length > 0 ? channelFilters.channels : undefined,
          exclude_channels: channelFilters.excludeChannels.length > 0 ? channelFilters.excludeChannels : undefined,
          preferred_channel: channelFilters.preferredChannel || undefined,
          contact_mode: channelFilters.contactMode || undefined,
          limit,
          offset,
        });
        if (!cancelled) {
          setPatients(response.patients);
          setTotal(response.total);
        }
      } catch (error: any) {
        if (!cancelled) {
          logger.error('patients_load_failed', { error: error.response?.data?.error || error.message });
          showError('Ошибка загрузки пациентов');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadPatients();

    return () => {
      cancelled = true;
    };
  }, [tenantId, searchQuery, dateFrom, dateTo, channelFilters, limit, offset, showError]);

  const handleOpenSelector = async (patient: PatientWithChannelsDTO) => {
    setSelectedPatient(patient);
    setSelectorOpen(true);
    setHistoryItems([]);
    setHistoryLoading(true);
    try {
      const items = await patientsApi.listPreferredChannelHistory(patient.id, 10);
      setHistoryItems(items);
    } catch {
      showError('Не удалось загрузить историю изменений');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveChannel = async (channel: string | null, contactMode: string, sourceCode: string) => {
    if (!selectedPatient) return;
    try {
      const response = await patientsApi.updatePreferredChannel(
        selectedPatient.id,
        channel,
        sourceCode,
        undefined,
        contactMode
      );
      showSuccess('Настройки сохранены');
      setPatients((prev) =>
        prev.map((patient) =>
          patient.id === selectedPatient.id
            ? {
                ...patient,
                preferred_channel: response.preferred_channel,
                preferred_channel_source_code: response.preferred_channel_source_code,
                preferred_channel_source_label: response.preferred_channel_source_label,
                preferred_channel_set_at: response.preferred_channel_set_at,
                contact_mode: contactMode,
              }
            : patient
        )
      );
    } catch (error: any) {
      showError(error.response?.data?.error || 'Ошибка сохранения');
      throw error;
    }
  };

  const getPreferredChannelLabel = (channel: string | null | undefined) => {
    if (!channel) return 'Auto';
    return getChannelDisplayName(channel);
  };

  const getSourceLabel = (patient: PatientWithChannelsDTO) => {
    if (patient.preferred_channel_source_label) {
      return patient.preferred_channel_source_label;
    }
    if (!patient.preferred_channel_source_code) {
      return '';
    }
    return preferenceSources.find((item) => item.code === patient.preferred_channel_source_code)?.display_name || patient.preferred_channel_source_code;
  };

  const getContactModeLabel = (mode: string | undefined) => {
    switch (mode) {
      case 'written_only':
        return '✎ письменно';
      case 'voice_only':
        return '☎ звонок';
      case 'do_not_contact':
        return '⊘ не беспокоить';
      default:
        return '∞ любой';
    }
  };

  return (
    <MainLayout headerTitle="Настройки пациентов" disableMainScroll>
      <LinearScreen
        eyebrow="Пациенты"
        title="Каналы уведомлений"
        lead="Индивидуальный канал пациента. Каждая смена требует источник и остаётся в истории: кто и зачем поменял."
        toolbar={
          <div className="space-y-3">
            {isSuperAdmin && (
              <div className="max-w-xs">
                <TenantSelector
                  value={selectedTenantId || ''}
                  onChange={setSelectedTenant}
                  required={false}
                  showLabel={false}
                />
              </div>
            )}
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Поиск по ФИО или телефону..."
              testId="patients-search"
            />
            <DateRangePicker
              from={dateFrom}
              to={dateTo}
              onChange={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
                setOffset(0);
              }}
              presets={[
                { label: 'Сегодня', from: startOfDay(new Date()), to: endOfDay(new Date()) },
                { label: 'Завтра', from: startOfDay(addDays(new Date(), 1)), to: endOfDay(addDays(new Date(), 1)) },
                { label: 'Неделя', from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: endOfWeek(new Date(), { weekStartsOn: 1 }) },
                { label: 'Месяц', from: startOfMonth(new Date()), to: endOfMonth(new Date()) },
              ]}
              testId="patients-date-range"
            />
            {(dateFrom || dateTo) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDateFrom(null);
                  setDateTo(null);
                  setOffset(0);
                }}
              >
                За все время
              </Button>
            )}
          </div>
        }
      >
        <Card>
          <CardContent className="pt-6">
            <p className="mb-4 text-xs text-shell-muted">
              Период фильтрует по последнему визиту. Если дата визита пустая, ищите по ФИО или сбросьте период.
            </p>
            <div className="mb-6">
              <PatientChannelFilters
                value={channelFilters}
                onChange={(next) => {
                  setChannelFilters(next);
                  setOffset(0);
                }}
                onReset={() => {
                  setChannelFilters({
                    matchMode: 'any',
                    channels: [],
                    excludeChannels: [],
                    preferredChannel: '',
                    contactMode: '',
                  });
                  setOffset(0);
                }}
              />
            </div>

            {isLoading ? (
              <LoadingState text="Загрузка пациентов..." />
            ) : patients.length === 0 ? (
              <EmptyState
                title="Пациенты не найдены"
                description={
                  dateFrom || dateTo
                    ? 'Сбросьте период на "За все время" или уточните поиск по ФИО/телефону.'
                    : 'Попробуйте изменить фильтры или поиск по ФИО/телефону.'
                }
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ФИО</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead>Каналы</TableHead>
                      <TableHead>Настройки</TableHead>
                      <TableHead>Последний визит</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patients.map((patient) => (
                      <TableRow
                        key={patient.id}
                        className={patient.contact_mode === 'do_not_contact' ? 'bg-red-50' : undefined}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{patient.full_name || '—'}</span>
                            {patient.contact_mode === 'do_not_contact' && (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                ⊘ DNC
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatPhone(patient.phone_normalized || patient.phone || '')}</TableCell>
                        <TableCell>
                          <ChannelBadges
                            channels={patient.available_channels}
                            hasCustomSettings={!!patient.preferred_channel}
                            preferredChannel={patient.preferred_channel}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell>
                          <Can
                            permission="settings:write"
                            fallback={
                              <span className="text-sm text-neutral-500">
                                {getPreferredChannelLabel(patient.preferred_channel)}
                              </span>
                            }
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void handleOpenSelector(patient)}
                              className="h-8 px-2 text-xs"
                              testId={`patient-settings-${patient.id}`}
                            >
                              <Settings2 className="mr-1.5 h-3.5 w-3.5" />
                              {getPreferredChannelLabel(patient.preferred_channel)}
                            </Button>
                          </Can>
                          {(patient.preferred_channel_source_code || patient.preferred_channel_set_at) && (
                            <div className="mt-1 text-xs text-neutral-500">
                              {getSourceLabel(patient) || 'Источник не указан'}
                              {patient.preferred_channel_set_at ? ` · ${formatDateTime(patient.preferred_channel_set_at)}` : ''}
                            </div>
                          )}
                          <div className="mt-1 text-xs text-neutral-500">
                            Режим: {getContactModeLabel(patient.contact_mode)}
                            {patient.contact_mode_set_at ? ` · ${formatDateTime(patient.contact_mode_set_at)}` : ''}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(patient.last_visit_at || patient.last_visit)
                            ? formatDateTime(patient.last_visit_at || patient.last_visit)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Pagination
                  offset={offset}
                  limit={limit}
                  total={total}
                  onOffsetChange={setOffset}
                  className="mt-4"
                  testId="patients-pagination"
                />
              </>
            )}
          </CardContent>
        </Card>
      </LinearScreen>

      <PatientChannelSelector
        isOpen={selectorOpen}
        onClose={() => {
          setSelectorOpen(false);
          setSelectedPatient(null);
          setHistoryItems([]);
          setHistoryLoading(false);
        }}
        patient={selectedPatient}
        sourceOptions={preferenceSources}
        historyItems={historyItems}
        historyLoading={historyLoading}
        onSave={handleSaveChannel}
      />
    </MainLayout>
  );
}
